import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, ClipboardCheck, FileText, Loader2, Plus } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api, { API_URL, formatApiError } from "@/lib/api";
import { DIRECTION_LABELS, FA_STATUS_COLORS, FA_STATUS_LABELS, SKILL_LABELS } from "@/config/finalCatalog";
import { ProgrammeStatusBadge } from "@/components/child/TrainingTab";

export default function FinalAssessmentTab({ child }) {
  const { hasPermission } = useAuth();
  const canAssess = hasPermission("final:assess");
  const canIssue = hasPermission("certificates:issue");
  const [programme, setProgramme] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState("outbound");
  const [saving, setSaving] = useState(false);
  const [reissueReason, setReissueReason] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/programmes");
      const mine = (data.items || []).filter((p) => p.child_id === child.id);
      const prog = mine.find((p) => !["cancelled", "archived"].includes(p.status)) || mine[0];
      setProgramme(prog || null);
      if (prog) {
        const [fas, cs] = await Promise.all([
          api.get("/final-assessments", { params: { programme_id: prog.id } }),
          api.get("/certificates", { params: { child_id: child.id } }),
        ]);
        setAssessments(fas.data.items || []);
        setCerts(cs.data.items || []);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [child.id]);

  const createFa = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/final-assessments/programmes/${programme.id}`, { direction });
      toast.success(`Final assessment ${data.final_number} created`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const issueCert = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/programmes/${programme.id}/certificate`);
      toast.success(`Certificate ${data.certificate_number} issued`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  const completed = programme?.status === "completed";
  const activeCert = certs.find((c) => ["issued", "reissued"].includes(c.status));
  const approvedDirections = programme?.outcome?.directions || [];

  return (
    <div className="space-y-4" data-testid="final-tab">
      {!programme ? (
        <Card><CardContent className="p-6"><p className="text-sm text-slate-600">No training programme yet — final assessment follows the training programme.</p></CardContent></Card>
      ) : completed ? (
        <Card className="border-ukgreen/40 bg-ukgreen-light/30" data-testid="completion-screen">
          <CardContent className="p-6 text-center space-y-3">
            <Award className="h-12 w-12 text-ukgreen mx-auto" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">Programme completed</h2>
            <p className="text-lg font-semibold text-slate-800">{child.first_name} {child.surname}</p>
            <p className="text-sm text-slate-600">
              Completed {programme.actual_completion_date} · {approvedDirections.map((d) => DIRECTION_LABELS[d]).join(" and ") || "—"}
            </p>
            {programme.outcome && (
              <p className="text-xs text-slate-500">
                Assessed by authorised assessor · approved by manager · certificate {activeCert ? `${activeCert.certificate_number} (v${activeCert.version}, ${activeCert.status})` : "not yet issued"}
              </p>
            )}
            <div className="flex flex-wrap justify-center gap-2 pt-2">
              {activeCert && (
                <a href={`${API_URL}/certificates/${activeCert.id}/pdf`} target="_blank" rel="noreferrer" data-testid="certificate-download"
                  className="inline-flex h-11 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-brand-hover">
                  <Award className="h-4 w-4" /> View certificate
                </a>
              )}
              {canIssue && !activeCert && (
                <Button data-testid="certificate-issue-button" onClick={issueCert} disabled={saving} className="h-11 bg-ukgreen text-white hover:bg-ukgreen/90">
                  <Award className="h-4 w-4 mr-1" /> Issue certificate
                </Button>
              )}
              {canIssue && activeCert && (
                <div className="flex items-center gap-2">
                  <input data-testid="certificate-reissue-reason" placeholder="Reissue reason" value={reissueReason}
                    onChange={(e) => setReissueReason(e.target.value)}
                    className="h-11 rounded-md border border-border px-3 text-sm" />
                  <Button variant="outline" data-testid="certificate-reissue-button" disabled={saving || !reissueReason.trim()}
                    onClick={async () => {
                      setSaving(true);
                      try {
                        await api.post(`/certificates/${activeCert.id}/reissue`, { reason: reissueReason });
                        toast.success("Certificate reissued — original preserved");
                        setReissueReason("");
                        load();
                      } catch (e) {
                        toast.error(formatApiError(e));
                      } finally {
                        setSaving(false);
                      }
                    }}>Reissue</Button>
                </div>
              )}
              <a href={`${API_URL}/programmes/${programme.id}/completion-report.pdf`} target="_blank" rel="noreferrer" data-testid="completion-report-link"
                className="inline-flex h-11 items-center gap-2 rounded-md border border-border px-4 text-sm font-semibold text-slate-700 hover:bg-muted">
                <FileText className="h-4 w-4" /> Completion report
              </a>
              {hasPermission("children:read_all") && (
                <a href={`${API_URL}/programmes/${programme.id}/council-summary.pdf`} target="_blank" rel="noreferrer" data-testid="council-summary-link"
                  className="inline-flex h-11 items-center gap-2 rounded-md border border-border px-4 text-sm font-semibold text-slate-700 hover:bg-muted">
                  <FileText className="h-4 w-4" /> Council summary
                </a>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-primary" /> Final independent travel assessment
            </CardTitle>
            {canAssess && programme.status === "final_assessment_due" && (
              <Button data-testid="final-create-button" onClick={() => setOpen(true)} className="h-11 bg-primary text-white font-semibold">
                <Plus className="h-4 w-4 mr-1" /> New final assessment
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-3">
              Programme {programme.programme_number} · <ProgrammeStatusBadge status={programme.status} />
              {programme.status !== "final_assessment_due" && (
                <span className="block mt-1 text-xs text-slate-500" data-testid="final-not-due">
                  Final assessment becomes available when a trainer's recommendation is approved by a manager (FINAL ASSESSMENT DUE). Completing 12 weeks never passes a child automatically.
                </span>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {programme && assessments.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Final assessments ({assessments.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {assessments.map((fa) => (
              <Link key={fa.id} to={`/final-assessments/${fa.id}`} data-testid={`final-row-${fa.final_number}`}
                className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3 hover:bg-muted transition-colors duration-200">
                <div className="min-w-[150px]">
                  <p className="font-mono font-semibold text-slate-900">{fa.final_number}</p>
                  <p className="text-xs text-slate-500">attempt {fa.attempt} · {DIRECTION_LABELS[fa.direction]}</p>
                </div>
                <p className="text-xs text-slate-600 flex-1">Assessor: {fa.assessor_name} · {fa.assessment_date || "date not set"}</p>
                <Badge className={FA_STATUS_COLORS[fa.status]}>{FA_STATUS_LABELS[fa.status]}</Badge>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>New final assessment</DialogTitle>
            <DialogDescription>
              Each journey direction is assessed separately — competence in one direction never implies competence in the other.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Journey direction *</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger data-testid="final-direction" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="outbound">Home → School</SelectItem>
                  <SelectItem value="return">School → Home</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="final-create-submit" className="h-11 w-full bg-primary text-white" disabled={saving} onClick={createFa}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create assessment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ComparisonTable({ comparison }) {
  const areas = Object.entries(comparison || {});
  if (!areas.length) return null;
  return (
    <div className="overflow-x-auto" data-testid="comparison-table">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-500 border-b border-border">
          <th className="py-2 pr-3">Area</th><th className="py-2 pr-3">Direction</th><th className="py-2 pr-3">Initial</th><th className="py-2">Final</th>
        </tr></thead>
        <tbody>
          {areas.flatMap(([area, dirs]) =>
            Object.entries(dirs).map(([dir, vals]) => (
              <tr key={`${area}-${dir}`} className="border-b border-border/50">
                <td className="py-2 pr-3 font-medium text-slate-800">{SKILL_LABELS[area] || area}</td>
                <td className="py-2 pr-3">{DIRECTION_LABELS[dir] || dir}</td>
                <td className="py-2 pr-3">{vals.initial ? `L${vals.initial}` : "—"}</td>
                <td className="py-2">{vals.final ? `L${vals.final}` : "—"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
